import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore, useTopicsStore, useCardsStore, useIndexCardsStore } from '@/stores'
import Layout from '@/components/Layout'
import LoginPage from '@/pages/LoginPage'
import CallbackPage from '@/pages/CallbackPage'
import ComingSoonPage from '@/pages/ComingSoonPage'
import DashboardPage from '@/pages/DashboardPage'
import TopicPage from '@/pages/TopicPage'
import CanvasPage from '@/pages/CanvasPage'
import CardPage from '@/pages/CardPage'
import NewCardPage from '@/pages/NewCardPage'
import SearchPage from '@/pages/SearchPage'
import InboxPage from '@/pages/InboxPage'
import IndexPage from '@/pages/IndexPage'
import IndexCardPage from '@/pages/IndexCardPage'
import IndexCardFormPage from '@/pages/IndexCardFormPage'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAllowed, isLoading } = useAuthStore()

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (!isAllowed) {
    return <Navigate to="/coming-soon" replace />
  }

  return <>{children}</>
}

function App() {
  const { setUser, setLoading } = useAuthStore()
  const { fetchTopics } = useTopicsStore()
  const { fetchCards } = useCardsStore()
  const { fetchIndexCards } = useIndexCardsStore()

  // Initialize auth state on app load
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check for stored tokens
        const idToken = localStorage.getItem('branka_id_token')
        const userJson = localStorage.getItem('branka_user')

        if (idToken && userJson) {
          const user = JSON.parse(userJson)
          setUser(user)

          // Fetch initial data after auth
          try {
            await Promise.all([fetchTopics(), fetchCards(), fetchIndexCards()])
          } catch (fetchError) {
            console.error('Failed to fetch initial data:', fetchError)
          }
        } else {
          setLoading(false)
        }
      } catch (error) {
        console.error('Auth init error:', error)
        setLoading(false)
      }
    }

    initAuth()
  }, [setUser, setLoading, fetchTopics, fetchCards, fetchIndexCards])

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/callback" element={<CallbackPage />} />
      <Route path="/coming-soon" element={<ComingSoonPage />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="topic/:topicId" element={<TopicPage />} />
        <Route path="topic/:topicId/canvas" element={<CanvasPage />} />
        <Route path="card/new" element={<NewCardPage />} />
        <Route path="card/:lid" element={<CardPage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="index" element={<IndexPage />} />
        <Route path="index/new" element={<IndexCardFormPage />} />
        <Route path="index/:id" element={<IndexCardPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
